import PizZip from 'pizzip';
import Docxtemplater from 'docxtemplater';
import { saveAs } from 'file-saver';
import { construireBudgetTemplate } from './ConstruireBudgetTemplate.js';
import { construireDonneesCommunes, estGenreFeminin, capitaliser } from '../construireDonneesCommunes.js';

const CHEMIN_TEMPLATE = '/templates/Convention_Etude.docx';

function accordRoleParGenre(role, estFeminin) {
  if (!role) return '';
  const roleStr = role.toLowerCase();

  if (roleStr.includes('chargé')) {
    return estFeminin ? role.replace(/Chargé/gi, 'Chargée') : role;
  }
  if (roleStr.includes('président')) {
    return estFeminin ? role.replace(/Président/gi, 'Présidente') : role;
  }
  if (roleStr.includes('trésorier')) {
    return estFeminin ? role.replace(/Trésorier/gi, 'Trésorière') : role;
  }
  return role;
}

function construireDonneesTemplate(formData, numDoc) {
  const { client, i2c } = formData.commun;
  const { projet, contraintes, exigences, moyensOutils } = formData.documents.conventionEtude;

  const donneesCommunes = construireDonneesCommunes(formData, 'CE', numDoc);

  // ── Auteur principal ─────────────────────────────────────
  const auteurPrincipal = i2c.contacts.find((c) => c.auteur) || { nom: '', prenom: '' };
  const mailAuteurPrincipal = auteurPrincipal.mail || '';

  // ── Déterminants complémentaires (spécifiques à la CE) ────
  const estClientFeminin = estGenreFeminin(client.genre);
  const detDuClient = estClientFeminin ? 'de la' : 'du';
  const detDuClientMaj = capitaliser(detDuClient);
  const detAuClient = estClientFeminin ? 'à la' : 'au';
  const detAuClientMaj = capitaliser(detAuClient);

  let libellePresidence;
  let mailsPresidence;
  if (donneesCommunes.estCopresidents) {
    libellePresidence = 'Coordonnées des Co-Présidents de la Junior';
    mailsPresidence = `${i2c.infoPresident1.mail} et ${i2c.infoPresident2.mail}`;
  } else {
    const estPresidente = estGenreFeminin(i2c.infoPresident1.genre);
    libellePresidence = estPresidente ? 'Coordonnées de la Présidente' : 'Coordonnées du Président';
    mailsPresidence = i2c.infoPresident1.mail;
  }

  const contactCharge = i2c.contacts.find((c) => c.role?.toLowerCase().includes('chargé')) || i2c.contacts[0];
  const estFemininContactCharge = estGenreFeminin(contactCharge?.genre);
  const detContactCharge = estFemininContactCharge ? 'la' : 'le';
  const detContactChargeMaj = capitaliser(detContactCharge);
  const nomContactCharge = estFemininContactCharge ? 'Chargée' : 'Chargé';
  const sonContactCharge = estFemininContactCharge ? 'sa' : 'son';
  const sonContactChargeMaj = capitaliser(sonContactCharge);
  const eleveIngenieur = estFemininContactCharge
    ? "Élève-ingénieure à l'École Nationale Supérieure de Cognitique (ENSC)"
    : "Élève-ingénieur à l'École Nationale Supérieure de Cognitique (ENSC)";

  const [contact1, contact2] = i2c.contacts;
  const estFemininContact1 = contact1 && estGenreFeminin(contact1.genre);
  const estFemininContact2 = contact2 && estGenreFeminin(contact2.genre);

  // ── Accord singulier/pluriel pour "Intervenant·e(s)" ──────
  
  const nombreIntervenantsNum = parseInt(formData.commun.nombreIntervenants, 10) || 0;
  const plusieursIntervenants = nombreIntervenantsNum > 1;
  const detIntervenants = plusieursIntervenants ? 'des' : "de l'";
  const detIntervenantsMaj = capitaliser(detIntervenants);
  const nomIntervenants = plusieursIntervenants ? 'Intervenants' : 'Intervenant';

  // ── Cahier des charges ─────────────────────────────────────
  const contraintesTemplate = contraintes.map((c, index) => ({
    nomContrainte: `C_${index + 1} - ${c.nom}`,
    sousPoints: c.description.split('\n').map((l) => l.trim()).filter(Boolean).map((texte) => ({ textePoint: texte })),
  }));

  const exigencesTemplate = exigences.map((e, index) => ({
    nomExigence: `EF_${index + 1} - ${e.nom}`,
    sousPoints: e.description.split('\n').map((l) => l.trim()).filter(Boolean).map((texte) => ({ textePoint: texte })),
  }));

  const livrablesTemplate = formData.commun.livrables.map((l) => ({
    nomLivrable: l.nom,
    garantieLivrable: l.garantie,
  }));

  // ── Budget ──────────────────────────────────────────────────
  const budgetData = construireBudgetTemplate(formData);

  return {
    ...donneesCommunes,

    nomAuteur: auteurPrincipal.nom,
    prenomAuteur: auteurPrincipal.prenom,
    mailAuteur: mailAuteurPrincipal,
    detDuClient,
    detDuClientMaj,
    detAuClient,
    detAuClientMaj,
    detContactCharge,
    detContactChargeMaj,
    nomContactCharge,
    sonContactCharge,
    sonContactChargeMaj,
    eleveIngenieur,
    libellePresidence,
    mailsPresidence,
    detIntervenants,
    detIntervenantsMaj,
    nomIntervenants,

    contexteProjet: projet.contexte,
    problematiqueProjet: projet.problematique,
    definitionBesoin: projet.definitionBesoin,
    critereValidation: projet.critereValidation,

    contraintes: contraintesTemplate,
    exigencesFonctionnelles: exigencesTemplate,
    moyensOutils,
    livrables: livrablesTemplate,

    ...budgetData,

    roleContactI2c1: accordRoleParGenre(contact1?.role || '', estFemininContact1),
    prenomNomContactI2c1: `${contact1?.prenom || ''} ${contact1?.nom || ''}`.trim(),
    descriptionContactI2c1: contact1?.description || '',
    mailContactI2c1: contact1?.mail || '',
    telContactI2c1: contact1?.tel || '',

    roleContactI2c2: accordRoleParGenre(contact2?.role || '', estFemininContact2),
    prenomNomContactI2c2: `${contact2?.prenom || ''} ${contact2?.nom || ''}`.trim(),
    descriptionContactI2c2: contact2?.description || '',
    mailContactI2c2: contact2?.mail || '',
    telContactI2c2: contact2?.tel || '',
  };
}

function trimStrings(obj) {
  if (typeof obj === 'string') return obj.trimEnd();
  if (Array.isArray(obj)) return obj.map(trimStrings);
  if (obj && typeof obj === 'object') {
    return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, trimStrings(v)]));
  }
  return obj;
}

async function genererConventionEtude(formData, numDoc) {
  const reponse = await fetch(CHEMIN_TEMPLATE);
  if (!reponse.ok) {
    throw new Error(`Impossible de charger le template (${reponse.status})`);
  }
  const zip = new PizZip(await reponse.arrayBuffer());
  const doc = new Docxtemplater(zip, { paragraphLoop: true, linebreaks: true });

  const donnees = trimStrings(construireDonneesTemplate(formData, numDoc));

  try {
    doc.render(donnees);
  } catch (error) {
    console.error('Erreur de génération du document :', error);
    if (error.properties && error.properties.errors) {
      console.error(JSON.stringify(error.properties.errors, null, 2));
    }
    throw error;
  }

  const blob = doc.getZip().generate({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });

  const nomFichier = `${donnees.refDocument}.docx`;
  saveAs(blob, nomFichier);
}

export default genererConventionEtude;